import {all, takeEvery, put, call, select} from 'redux-saga/effects';
import {history} from '../../../index';
import * as jwt from '../../jwt'
import actions from './actions';

const mapAuthProviders = {
    jwt: {
        login: jwt.login,
        register: jwt.register,
        currentAccount: jwt.currentAccount,
        logout: jwt.logout,
        changePassword: jwt.changePassword,
    },
};

// The token response carries this as a string.
const changeRequired = result =>
    !!result && (result.pwd_change_required === 'true' || result.pwd_change_required === true);

export function* LOGIN({payload}) {
    const {UserName, Password, captchaToken} = payload;
    yield put({
        type: 'user/SET_STATE',
        payload: {
            loading: true,
        },
    });
    const {authProvider: autProviderName} = yield select(state => state.settings);
    const success = yield call(mapAuthProviders[autProviderName].login, UserName, Password, captchaToken);
    if (success) {
        const mustChange = changeRequired(success);
        yield put({
            type: 'user/SET_STATE',
            payload: {
                passwordChangeRequired: mustChange,
                passwordChangeError: '',
                username: UserName,
            },
        });
        yield put({
            type: 'user/LOAD_CURRENT_ACCOUNT',
            payload: payload
        });
        // A token carrying pwd_change_required is rejected on every route except
        // the change endpoint, so sending it to the dashboard would only produce
        // a screen of 403s. The layout renders the change screen off the same
        // flag; this navigate just keeps the address bar honest.
        payload.navigate(mustChange ? '/' : '/dashboard')
    }
    if (!success) {
        yield put({
            type: 'user/SET_STATE',
            payload: {
                loading: false,
            },
        });
    }
}

export function* REGISTER({payload}) {
    const {email, password, name} = payload;
    yield put({
        type: 'user/SET_STATE',
        payload: {
            loading: true,
        },
    });
    const {authProvider} = yield select(state => state.settings);
    const success = yield call(mapAuthProviders[authProvider].register, email, password, name);
    if (success) {
        yield put({
            type: 'user/LOAD_CURRENT_ACCOUNT',
        });
        yield history.push('/');
    }
    if (!success) {
        yield put({
            type: 'user/SET_STATE',
            payload: {
                loading: false,
            },
        });
    }
}

export function* LOAD_CURRENT_ACCOUNT() {
    yield put({
        type: 'user/SET_STATE',
        payload: {
            loading: true,
        },
    });
    const {authProvider} = yield select(state => state.settings);
    const response = yield call(mapAuthProviders[authProvider].currentAccount);
    if (response) {
        const {id, email, name, avatar, role} = response;
        yield put({
            type: 'user/SET_STATE',
            payload: {
                id,
                name,
                email,
                avatar,
                role,
                authorized: true,
            },
        });
    } else {
        if (window.location.href !== window.location.origin + "/")
            window.location = window.location.origin;
    }
    yield put({
        type: 'user/SET_STATE',
        payload: {
            loading: false,
        },
    });
}

export function* CHANGE_PASSWORD({payload}) {
    const {currentPassword, newPassword, navigate} = payload;
    yield put({
        type: 'user/SET_STATE',
        payload: {loading: true, passwordChangeError: ''},
    });

    const {authProvider} = yield select(state => state.settings);
    const result = yield call(mapAuthProviders[authProvider].changePassword, currentPassword, newPassword);

    if (!result || !result.response) {
        yield put({
            type: 'user/SET_STATE',
            payload: {
                loading: false,
                passwordChangeError: (result && result.message) || 'Unable to change the password.',
            },
        });
        return;
    }

    // The password is now changed server-side, but this token still carries
    // pwd_change_required and would keep being refused everywhere. Trade it for a
    // clean one by signing in again with the new password.
    const username = yield select(state => state.user.username);
    const captchaToken = 'ea4c6148-3414-4105-bcfa-9cdc7337bb3b';
    const reLogin = yield call(mapAuthProviders[authProvider].login, username, newPassword, captchaToken);

    if (reLogin && !changeRequired(reLogin)) {
        yield put({
            type: 'user/SET_STATE',
            payload: {
                loading: false,
                passwordChangeRequired: false,
                passwordChangeError: '',
            },
        });
        yield put({type: 'user/LOAD_CURRENT_ACCOUNT'});
        if (navigate) navigate('/dashboard');
        return;
    }

    // Re-login did not produce a clean token. Drop the session rather than leave
    // the user holding a restricted one, and make them sign in by hand.
    yield put({type: 'user/LOGOUT'});
    yield put({
        type: 'user/SET_STATE',
        payload: {
            loading: false,
            passwordChangeError: 'Your password was changed. Please sign in again.',
        },
    });
}

export function* LOGOUT() {
    const {authProvider} = yield select(state => state.settings);
    yield call(mapAuthProviders[authProvider].logout);
    yield put({
        type: 'user/SET_STATE',
        payload: {
            id: '',
            name: '',
            role: '',
            email: '',
            avatar: '',
            authorized: false,
            loading: false,
            passwordChangeRequired: false,
            username: '',
        },
    });
}

export default function* rootSaga() {
    yield all([
        takeEvery(actions.LOGIN, LOGIN),
        takeEvery(actions.REGISTER, REGISTER),
        takeEvery(actions.LOAD_CURRENT_ACCOUNT, LOAD_CURRENT_ACCOUNT),
        takeEvery(actions.LOGOUT, LOGOUT),
        takeEvery(actions.CHANGE_PASSWORD, CHANGE_PASSWORD),
        LOAD_CURRENT_ACCOUNT(), // run once on app load to check user auth
    ]);
}
